<template>
  <div class="solar-plant-inverters">
    <div v-if="!inverters || inverters.length === 0" class="text-muted text-center py-3">
      <i class="fa fa-bolt fa-2x mb-2 d-block"></i>
      {{ $t('no_inverters_added') }}
    </div>
    <div v-else>
      <b-row
        v-for="(inverter, index) in inverters"
        :key="getInverterKey(inverter, index)"
        class="inverter-row mb-2"
      >
        <b-col lg="10" md="6" sm="12">
          <div class="d-flex align-items-center">
            <div class="inverter-info flex-grow-1">
              <router-link
                v-if="inverter.item && inverter.item.number"
                target="_blank"
                :to="{
                  name: 'ItemEditPage',
                  params: { itemNumber: inverter.item.number },
                }"
                class="mr-2"
              >
                #{{ inverter.item.number.toString().padStart(4, '0') }}
                <i class="fa fa-sm fa-external-link"></i>
              </router-link>
              <span v-if="getManufacturer(inverter.item)">
                {{ getManufacturer(inverter.item) }} - {{ getType(inverter.item) }}
              </span>
              <span v-else class="text-muted">-</span>
              <span v-if="getNominalPower(inverter.item)" class="ml-2 text-muted">
                ({{ getNominalPower(inverter.item) }} kW)
              </span>
            </div>
            <div class="quantity-controls d-flex align-items-center ml-3">
              <button
                class="btn btn-sm btn-danger"
                @click="decreaseCount(index)"
                :disabled="inverter.count <= 1"
              >
                <i class="fa fa-minus"></i>
              </button>
              <input
                v-model.number="inverter.count"
                type="number"
                min="1"
                class="form-control form-control-sm mx-1 quantity-input"
                @input="updateCount(index, $event.target.value)"
              />
              <button class="btn btn-sm btn-success" @click="increaseCount(index)">
                <i class="fa fa-plus"></i>
              </button>
            </div>
            <button
              class="btn btn-sm btn-danger ml-2"
              @click="removeInverter(index)"
              :title="$t('remove')"
            >
              <i class="fa fa-trash"></i>
            </button>
          </div>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <div class="text-center border p-1">
            <span v-if="getTotalPower(inverter)">{{ getTotalPower(inverter) }} kW</span>
            <span v-else class="text-muted">-</span>
          </div>
        </b-col>
      </b-row>

      <!-- Total Power Row -->
      <b-row class="mt-3">
        <b-col lg="10" md="6" sm="12">
          <div class="text-right py-1">
            <strong>{{ $t('inverter') }} {{ $t('totalPower') }}</strong>
          </div>
        </b-col>
        <b-col lg="2" md="6" sm="12">
          <div class="text-center border p-1 bg-light">
            <strong>{{ totalNominalPower }} kW</strong>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { formatInverterTotalNominalPower } from '@/helpers/solarPlant';

export default {
  name: 'SolarPlantInverters',
  props: {
    inverters: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    totalCount() {
      return this.inverters.reduce((total, inv) => total + parseInt(inv.count || 0, 10), 0);
    },
    totalNominalPower() {
      return formatInverterTotalNominalPower(this.inverters);
    },
  },
  methods: {
    getInverterKey(inverter, index) {
      return inverter.item?._id || inverter.item || `inv-${index}`;
    },
    getManufacturer(item) {
      return item?.inverter?.manufacturer || '-';
    },
    getType(item) {
      return item?.inverter?.type || '-';
    },
    getNominalPower(item) {
      return item?.inverter?.nominalPower || null;
    },
    getTotalPower(inverter) {
      const power = this.getNominalPower(inverter.item);
      if (!power) return null;
      const total = power * parseInt(inverter.count || 1, 10);
      return this.$n(total, 'decimal_single');
    },
    increaseCount(index) {
      const updatedInverters = [...this.inverters];
      updatedInverters[index] = {
        ...updatedInverters[index],
        count: parseInt(updatedInverters[index].count || 0, 10) + 1,
      };
      this.$emit('update-inverters', updatedInverters);
    },
    decreaseCount(index) {
      const updatedInverters = [...this.inverters];
      const currentCount = parseInt(updatedInverters[index].count || 0, 10);
      if (currentCount > 1) {
        updatedInverters[index] = {
          ...updatedInverters[index],
          count: currentCount - 1,
        };
        this.$emit('update-inverters', updatedInverters);
      }
    },
    updateCount(index, value) {
      const count = parseInt(value, 10);
      if (count >= 1) {
        const updatedInverters = [...this.inverters];
        updatedInverters[index] = {
          ...updatedInverters[index],
          count: count,
        };
        this.$emit('update-inverters', updatedInverters);
      }
    },
    removeInverter(index) {
      const updatedInverters = this.inverters.filter((_, i) => i !== index);
      this.$emit('update-inverters', updatedInverters);
    },
  },
};
</script>

<style scoped lang="scss">
.quantity-input {
  width: 60px;
  text-align: center;
}

.quantity-controls {
  gap: 4px;
}

.inverter-row {
  padding: 0.25rem 0;

  &:hover {
    background-color: #f8f9fa;
  }
}

.inverter-info {
  min-height: 32px;
  display: flex;
  align-items: center;
}
</style>
